import { debounce } from "lodash";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
} from "react";
import { CaptureEditorHost } from "../../Components/CaptureEditor/CaptureEditorHost";
import CSSInjector from "../../Components/CSSInjector/CSSInjector";
import FormLoading from "../../Components/FormLoading/FormLoading";
import TabBar from "../../Components/TabBar/TabBar";
import WidgetHeader from "../../Components/WidgetHeader/WidgetHeader";
import Communicator from "../../Helper/Communicator";
import { useAppViewStore } from "../../Stores/AppView";
import { useCaptureStore } from "../../Stores/Capture";
import { useConfigStore } from "../../Stores/Config";
import { useKaiVoiceStore } from "../../Stores/KaiVoice";
import { AppMode, useRouterStore } from "../../Stores/Router";
import { useTicketStore } from "../../Stores/Ticket";
import AppModalView from "../AppModalView/AppModalView";
import AppView from "../AppView/AppView";
import ChatbarView from "../Chatbar/ChatbarView";
import ChecklistDetails from "../ChecklistDetails/ChecklistDetails";
import Checklists from "../Checklists/Checklists";
import Conversation from "../Conversation/Conversation";
import ConversationLoading from "../ConversationLoading/ConversationLoading";
import Conversations from "../Conversations/Conversations";
import CustomView from "../CustomView/CustomView";
import FeatureRequestDetails from "../FeatureRequestDetails/FeatureRequestDetails";
import FeedbackFlow from "../FeedbackFlow/FeedbackFlow";
import HelpCenter from "../HelpCenter/HelpCenter";
import Home from "../Home/Home";
import { homeVersionClass } from "../Home/homeVersions";
import News from "../News/News";
import NewsDetails from "../NewsDetails/NewsDetails";
import Roadmap from "../Roadmap/Roadmap";
import SurveyV2 from "../SurveyV2/SurveyV2";
import KaiVoiceCall from "../KaiVoiceCall/KaiVoiceCall";
import "./App.scss";


// `inert` isn't in React 18's typings yet; the attribute form ("") is what
// browsers honour. Applied to the widget chrome while the voice overlay is up
// so focus/screen readers can't wander behind the dialog.
const inertProps = { inert: "" } as any;

// Remount key for the routed page so the entrance animation replays on every
// navigation (renderMainComponents reuses the DOM node otherwise). Help-center
// sub-pages collapse onto one key so browsing inside it doesn't remount and
// drop the view's scroll/search state.
const contentKey = (page: string) =>
  page.startsWith("helpcenter") ? "helpcenter" : page;

export const usePrevious = (value: any) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });

  return ref.current as any;
};

function App() {
  const routerStore = useRouterStore();
  const configStore = useConfigStore();
  const aiTakeoffActive = useTicketStore((state) => state.aiTakeoffActive);
  // The nav highlight (TabBar) reads currentPage directly, so it flips on the
  // tap frame. The (potentially heavy) routed page instead renders from a
  // DEFERRED copy of currentPage: React commits the cheap highlight update and
  // paints it first, then renders the incoming view in a follow-up, lower
  // priority pass. This is the fix for "the nav focus lags until the page
  // loads" — the highlight no longer shares one blocking commit with the view.
  const deferredPage = useDeferredValue(routerStore.currentPage);
  // A live Kai Voice call mounts the full-dark call surface OVER the whole
  // widget (header, page, tab bar) at this level, so it survives page
  // navigation and widget close/reopen, and no theme-colored chrome (header
  // text, back button) bleeds through the dark surface. The WebRTC session
  // lives in the KaiVoice store, untouched by whatever renders underneath.
  const voiceCallState = useKaiVoiceStore((state) => state.callState);
  const voiceCallCoversApp = voiceCallState !== "idle";
  // The capture annotation editor is a full-screen dialog over the widget;
  // the widget underneath is inert while it's open.
  const captureEditorOpen = useCaptureStore((state) => !!state.editor);
  const [scrolled, setScrolled] = useState(false);
  const appViewStore = useAppViewStore();
  const [headerOpacity, setHeaderOpacity] = useState(1.0);
  const contentRef = useRef<HTMLDivElement>(null);

  /**
   * Human-readable name of the page currently shown, used to label the main
   * content region so screen readers announce where the user landed after
   * navigating. Reuses the existing menu labels so it stays localized.
   */
  const getCurrentPageLabel = (): string | undefined => {
    const page = routerStore.currentPage;
    const c = configStore.config;
    if (page === "menu") {
      return configStore.translateText("homeLabel");
    }
    if (
      page === "conversations" ||
      page === "conversation" ||
      page === "conversationloading"
    ) {
      return c.allConversationsMenuLabel;
    }
    if (page === "news" || page === "newsdetails") {
      return c.newsMenuLabel;
    }
    if (page === "checklists" || page === "checklistdetails") {
      return c.checklistMenuLabel;
    }
    if (page === "roadmap" || page === "feature-request-details") {
      return c.featureRequestsMenuLabel;
    }
    if (page.startsWith("helpcenter")) {
      return c.knowledgeBaseMenuLabel;
    }
    return undefined;
  };

  const updateAnimationStatus = (animating: boolean) => {
    if (document && document.body) {
      if (animating) {
        document.body.classList.add("animating");
      } else {
        document.body.classList.remove("animating");
      }
    }

    const htmlRootNodes = document.getElementsByTagName("html");
    if (htmlRootNodes && htmlRootNodes.length > 0) {
      if (animating) {
        htmlRootNodes[0].classList.add("animating");
      } else {
        htmlRootNodes[0].classList.remove("animating");
      }
    }
  };

  const resetBodyScroll = () => {
    updateAnimationStatus(false);
  };

  const resetBodyScrollDebounced = useCallback(
    debounce(() => resetBodyScroll(), 1000),
    [],
  );

  const escFunction = useCallback((event: any) => {
    if (event.key === "Escape") {
      // The Surveys 2.0 renderer owns Escape (closes the card only, and
      // flushes the unsaved answer first).
      if (useRouterStore.getState().currentPage === "survey") {
        return;
      }
      Communicator.closeWidget();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);

    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, []);

  useEffect(() => {
    if (routerStore.currentPage) {
      updateAnimationStatus(true);
      resetBodyScrollDebounced();
      // Move screen-reader / keyboard focus to the top of the newly shown page
      // so navigation isn't a trap for assistive-technology users. But if the new
      // page already moved focus into itself (e.g. an auto-focused search input),
      // respect that rather than stealing it back. preventScroll keeps the
      // existing anti-scroll-jump behaviour intact.
      const container = contentRef.current;
      if (container && !container.contains(document.activeElement)) {
        container.focus({ preventScroll: true });
      }
    }
  }, [routerStore.currentPage]);

  // WCAG 3.1.1 (Language of Page): keep the document language in sync with the
  // widget's configured language so screen readers use the correct pronunciation,
  // instead of the hardcoded `en` in index.html.
  useEffect(() => {
    if (typeof document === "undefined" || !document.documentElement) {
      return;
    }
    const lang = configStore.getCurrentLanguage(true);
    if (lang) {
      document.documentElement.lang = lang;
    }
    document.documentElement.dir = configStore.isRTLLang() ? "rtl" : "ltr";
  }, [
    configStore.config?.localizationOptions?.language,
    configStore.config?.localizationOptions?.rtl,
  ]);

  const renderMainComponents = () => {
    // Renders from the deferred page so the incoming view lands in a follow-up
    // pass while the nav highlight paints immediately (see deferredPage above).
    const page = deferredPage;
    if (page === "flow") {
      return <FeedbackFlow />;
    } else if (page === "survey-inline") {
      // A gleap://survey link inside the messenger: the survey as a page, under the header.
      return <SurveyV2 />;
    } else if (page === "conversations") {
      return <Conversations />;
    } else if (page === "conversation") {
      return <Conversation />;
    } else if (page === "conversationloading") {
      return <ConversationLoading />;
    } else if (page === "news") {
      return <News />;
    } else if (page === "newsdetails") {
      return <NewsDetails />;
    } else if (page === "checklists") {
      return <Checklists />;
    } else if (page === "roadmap") {
      return <Roadmap />;
    } else if (page === "feature-request-details") {
      return <FeatureRequestDetails />;
    } else if (page === "checklistdetails") {
      return <ChecklistDetails />;
    } else if (
      page === "helpcenter" ||
      page === "helpcenter-collection" ||
      page === "helpcenter-article" ||
      page === "helpcenter-search"
    ) {
      return <HelpCenter />;
    } else if (page === "appview") {
      return <AppView />;
    } else if (page === "custom-view") {
      return <CustomView />;
    } else {
      // Show loading spinner while navigating to configured start page.
      if (routerStore.startPageLoading) {
        return (
          <div className="start-page-loading">
            <FormLoading />
          </div>
        );
      }

      return <Home />;
    }
  };

  const showTabBar = () => {
    if (routerStore.appMode === AppMode.CHATBAR) {
      return false;
    }
    // The takeoff (home ask-input → conversation) glides the composer down
    // over the tab bar area; fade the bar out as soon as it starts, and keep
    // it hidden on the loading shell the takeoff lands on.
    if (aiTakeoffActive) {
      return false;
    }
    if (
      [
        "flow",
        "survey-inline",
        "newsdetails",
        "conversation",
        "conversationloading",
        "custom-view",
      ].includes(routerStore.currentPage)
    ) {
      return false;
    }
    if (appViewStore.pageStack.length > 1) {
      return false;
    }
    return true;
  };

  if (routerStore.appMode === AppMode.CHATBAR) {
    return (
      <>
        <CSSInjector />
        <div
          className="main-app main-app--chatbar"
          dir={configStore.isRTLLang() ? "rtl" : "ltr"}
        >
          <ChatbarView />
        </div>
        <AppModalView />
      </>
    );
  }

  // Surveys 2.0: the renderer brings its own chrome (card / full screen /
  // page) — no widget header, tab bar or page container around it.
  if (routerStore.currentPage === "survey") {
    return (
      <>
        <CSSInjector />
        <SurveyV2 />
      </>
    );
  }

  const versionClass = homeVersionClass(configStore.getHomeVersion());

  return (
    <>
      <CSSInjector />
      <div
        className={`main-app ${
          routerStore.appMode === AppMode.SURVEY && "main-app--survey"
        } ${!showTabBar() && "main-app--tabbar--hidden"} ${
          !routerStore.showBackButton && "main-app--tabbar--suppressed"
        } ${
          (routerStore.appMode === AppMode.SURVEY_FULL ||
            routerStore.appMode === AppMode.SURVEY_WEB) &&
          "main-app--survey-full"
        } ${routerStore.currentPage === "menu" && "main-app--main"} ${
          versionClass ? `main-app--${versionClass}` : ""
        } ${voiceCallCoversApp ? "main-app--voice-call" : ""}`}
        dir={configStore.isRTLLang() ? "rtl" : "ltr"}
        onScroll={(e) => {
          if (e.currentTarget.scrollTop > 170) {
            setScrolled(true);
          } else {
            setScrolled(false);
          }

          var opacity = 1 - e.currentTarget.scrollTop / 200;
          if (opacity < 0) {
            opacity = 0;
          }
          if (opacity > 1) {
            opacity = 1;
          }
          setHeaderOpacity(opacity);
        }}
      >
        {/* display:contents keeps the flex-column layout identical; the
            wrapper only exists to carry `inert` while the call overlay is up. */}
        <div
          className="main-app--chrome"
          style={{ display: "contents" }}
          {...(voiceCallCoversApp || captureEditorOpen ? inertProps : {})}
        >
          <WidgetHeader
            headerOpacity={headerOpacity}
            scrolled={scrolled}
            page={deferredPage}
          />
          <div
            key={contentKey(deferredPage)}
            ref={contentRef}
            className={`content-container animation-type-${routerStore.navigationDirection}`}
            role="main"
            aria-label={getCurrentPageLabel()}
            tabIndex={-1}
          >
            {renderMainComponents()}
          </div>
          {/* Keyed like Home: replay the bar's entrance when the widget is
              actually revealed, not at invisible cold-boot mount time. */}
          <TabBar key={routerStore.widgetOpenCount} />
        </div>
        {voiceCallCoversApp && <KaiVoiceCall />}
      </div>
      <AppModalView />
      <CaptureEditorHost />
    </>
  );
}

export default App;
